// myumaps.ca/mobile — the landing page for the UMaps app.
//
// One screen, read top to bottom on a phone: what the app is, what it looks
// like, and the two buttons that get it. Almost everybody arriving here is on
// the device they are about to install on, so the layout is phone-first and the
// desktop version is the same column with more air around it.
//
// ⚠️ NO FORM, NO EMAIL. Until the 2026-10-05 store release this page was the
// beta's front door: a school address for TestFlight, two addresses for Play's
// closed-testing allow-list, and a wait while the operator pasted them into a
// console. The app is on both stores now, so the page asks nothing of anybody
// — it is the App Store button and the Google Play button, the visitor's own
// platform first, and the shortest possible path to tapping one. The beta
// signup endpoint (`/api/mobile-signup`) still exists for the operator's
// records; nothing here calls it.
//
// The one thing that still needs a word is the beta itself: a TestFlight build
// never receives the App Store release, and a Play tester still enrolled who
// reinstalls from the listing is served the beta again. So a fold-out under
// the buttons says what to do, in the same steps the app's own sunset screen
// gives (UMapsMobile `src/betaSunset/policy.ts`) — folded, because it is for
// the few hundred testers and not for the campus.
//
// A referral link (`/mobile/refer/<code>`) used to be credited when the signup
// landed. There is no signup, so it is credited on the STORE TAP instead —
// `POST /api/referral` — once per visit, fire-and-forget, never blocking the
// tap. See api/referral.js for the limits.

import { useCallback, useEffect, useRef, useState } from 'react';
import UMapsWordmark from './components/UMapsWordmark.jsx';
import { APP_STORE_URL, PLAY_STORE_URL, PLAY_LEAVE_BETA_URL, detectOs } from './utils/appStores.js';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

// The two listings and the platform detector are shared with the download
// popup on /umanitoba (utils/appStores.js, imported above); re-exported so the
// page's test and anything else that learned them here still finds them.
export { APP_STORE_URL, PLAY_STORE_URL, detectOs };

/**
 * The referral code a path carries, or ''.
 *
 * Validated to the exact shape the app mints, so a hand-typed or truncated URL
 * simply is not a referral rather than sending junk to the API. `/mobile/rf`
 * and a plain `/mobile` both yield '' and behave identically: an uncredited
 * visit, which is what the page was before referrals existed. Read once when
 * the page mounts (`useState` initialiser below), not at module load, so the
 * page is a function of the URL it was opened on and nothing else.
 */
export function referCodeFromPath(pathname) {
  const m = /^\/mobile\/refer\/([a-z0-9]{8})$/.exec(String(pathname || '').replace(/\/$/, '').toLowerCase());
  return m ? m[1] : '';
}

/**
 * The carousel. Captions name what is ON the screenshot rather than selling it
 * — somebody is looking at the picture while they read the words, and a slogan
 * that does not match what they can see is worse than no caption.
 *
 * Ordered strongest first: the indoor turn-by-turn is the thing no other app on
 * a student's phone does at all.
 */
const SHOTS = [
  { src: '/MobileImages/web/img_6699.webp', title: 'Step by step, floor by floor',
    body: 'Directions that keep going indoors. Which corridor, which stairs, which level.' },
  { src: '/MobileImages/web/img_6696.webp', title: 'Find any room, and the bus home',
    body: 'Search every room on campus, then live departures from the stop outside.' },
  { src: '/MobileImages/web/img_6679.webp', title: 'See which rooms are free',
    body: 'The whole timetable on the map. Green is free right now, red is in use.' },
  { src: '/MobileImages/web/img_6681.webp', title: 'Line your schedule up with friends',
    body: 'Share your timetable and find the gaps you actually have in common.' },
  { src: '/MobileImages/web/img_6693.webp', title: 'Campus menus and hours',
    body: 'What each place serves, what it costs, and whether it is open now.' },
  { src: '/MobileImages/web/img_6684.webp', title: 'Widgets on your home screen',
    body: 'Your next class, where it is, and the next buses, without opening anything.' },
];

const AUTOPLAY_MS = 4200;

export default function MobilePage() {
  const [referCode] = useState(() => referCodeFromPath(window.location.pathname));
  return (
    <div style={S.page}>
      <style>{CSS}</style>
      <div style={S.wrap}>
        <header style={S.header}>
          <UMapsWordmark size={26} href="/" style={{ fontSize: 22, color: '#1a56c4', letterSpacing: '-0.02em' }} />
          {/* The wordmark already says "UMaps", so the pill carries only the
              word that finishes it: the header reads "UMaps App". Repeating the
              name inside the badge beside the name would say it twice. */}
          <span style={S.beta}>App</span>
        </header>

        {/* Says the link worked, without claiming to know who sent it — the
            server never learns whose code a link carries, so "a friend" is the
            most this can honestly say. It sits above the headline because it is
            the answer to "why am I here", which is the first question somebody
            arriving from a group chat has. */}
        {!!referCode && (
          <div style={S.referred}>
            A student shared UMaps with you — install it from the button below and they&rsquo;ll
            know it helped.
          </div>
        )}

        <h1 style={S.h1}>The must-have app for students at the UofM!</h1>
        <p style={S.sub}>
          Schedules, classrooms, busses. UMaps is your campus in your pocket.
        </p>

        <Carousel />

        <Install referCode={referCode} />

        <footer style={S.footer}>
          <a style={S.footLink} href="/umanitoba">Open the web map instead →</a>
          {/* The second question, after "how do I get it", is "is this being
              worked on" — and the changelog is the only page that answers it. */}
          <a style={S.footLink} href="/mobile/changelog">What&rsquo;s new</a>
          <a style={S.footLink} href="/mobile-privacy-policy">Privacy policy</a>
        </footer>
      </div>
    </div>
  );
}

// ── Carousel ─────────────────────────────────────────────────────────────────
/**
 * A peek carousel: the current screenshot at full size, its neighbours smaller
 * and faded either side, so it reads as one list being scrolled rather than as
 * six unrelated pictures appearing in the same box. The edges are what tell you
 * there is more to see without a single word of instruction.
 *
 * ⚠️ Built on native scroll-snap, NOT a transform track. The reason is the
 * phone: scroll-snap gives real momentum swiping, rubber-banding at the ends
 * and per-pixel finger tracking for free, all of which a hand-rolled
 * touchstart/touchend handler approximates badly. Autoplay is then just a
 * smooth `scrollTo`, and the active slide is read back OUT of the scroll
 * position — so a finger and the timer can never disagree about which slide is
 * showing.
 *
 * The caption sits outside the rail rather than under each slide, because at
 * 62% width there is no room for two lines of text under a phone screenshot,
 * and a caption that scrolled with the images would be half-visible either side
 * of the one being read.
 */
function Carousel() {
  const [i, setI] = useState(0);
  const [stopped, setStopped] = useState(false);
  const railRef = useRef(null);
  // Set while the timer's own smooth scroll is in flight, so the scroll handler
  // does not mistake it for the reader taking over.
  const autoScrolling = useRef(false);

  const reduced = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const scrollTo = useCallback((n, smooth = true) => {
    const rail = railRef.current;
    const slide = rail?.children?.[n];
    if (!rail || !slide) return;
    // Centre the slide in the rail rather than aligning it left: the whole
    // point is that the neighbours show equally on both sides.
    const left = slide.offsetLeft - (rail.clientWidth - slide.clientWidth) / 2;
    autoScrolling.current = true;
    rail.scrollTo({ left, behavior: smooth && !reduced ? 'smooth' : 'auto' });
    // No scrollend event on Safari, so the flag is cleared on a timer sized to
    // outlast a smooth scroll.
    setTimeout(() => { autoScrolling.current = false; }, smooth && !reduced ? 600 : 60);
  }, [reduced]);

  // Which slide is centred, read back from the scroll position — the one source
  // of truth, whether the finger or the timer put it there.
  const onScroll = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const mid = rail.scrollLeft + rail.clientWidth / 2;
    let best = 0, bestD = Infinity;
    for (let n = 0; n < rail.children.length; n++) {
      const el = rail.children[n];
      const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - mid);
      if (d < bestD) { bestD = d; best = n; }
    }
    setI(best);
  }, []);

  useEffect(() => {
    if (stopped || reduced) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setI(prev => {
        const next = (prev + 1) % SHOTS.length;
        scrollTo(next);
        return next;
      });
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [stopped, reduced, scrollTo]);

  const shot = SHOTS[i] || SHOTS[0];

  // Wrap around, so neither arrow is ever a dead button. The rail itself can't
  // wrap (it is a real scroller with real ends), but the arrows are the
  // deliberate control and a disabled one at each end is a worse answer.
  const step = (d) => {
    const next = ((i + d) % SHOTS.length + SHOTS.length) % SHOTS.length;
    setStopped(true);
    setI(next);
    scrollTo(next);
  };

  return (
    <div className="mb-carousel">
      {/* A desktop reader has no swipe, and horizontal scrolling with a mouse
          means holding shift. The side screenshots are clickable, but only the
          arrows say so at a glance. Hidden below 620px, where a finger is the
          better control and two buttons would just cover the phones. */}
      <button className="mb-arrow prev" onClick={() => step(-1)} aria-label="Previous screenshot">
        <Chevron dir="left" />
      </button>
      <button className="mb-arrow next" onClick={() => step(1)} aria-label="Next screenshot">
        <Chevron dir="right" />
      </button>
      <div
        ref={railRef}
        className="mb-rail"
        onScroll={onScroll}
        // A touch or a drag hands the carousel over for good. Anything else
        // would mean the timer yanking the list out from under a finger.
        onPointerDown={() => setStopped(true)}
        role="group"
        aria-roledescription="carousel"
        aria-label="App screenshots"
      >
        {SHOTS.map((s, n) => (
          <button
            type="button"
            className={`mb-slide${n === i ? ' on' : ''}`}
            key={s.src}
            // A faded neighbour is the obvious thing to tap, so tapping brings
            // it to the middle instead of doing nothing.
            onClick={() => { setStopped(true); setI(n); scrollTo(n); }}
            aria-label={s.title}
            aria-current={n === i}
          >
            <img
              className="mb-shot"
              src={s.src}
              alt={s.title}
              // The real intrinsic size, so the browser reserves the right box
              // before the image lands and the page below does not jump.
              width={640}
              height={1385}
              loading={n < 2 ? 'eager' : 'lazy'}
              decoding="async"
            />
          </button>
        ))}
      </div>

      <div className="mb-cap" aria-live="polite">
        <span className="mb-cap-title">{shot.title}</span>
        <span className="mb-cap-body">{shot.body}</span>
      </div>

      <div className="mb-dots">
        {SHOTS.map((s, n) => (
          <button
            key={s.src}
            className={`mb-dot${n === i ? ' on' : ''}`}
            onClick={() => { setStopped(true); setI(n); scrollTo(n); }}
            aria-label={`Screenshot ${n + 1}: ${s.title}`}
            aria-current={n === i}
          />
        ))}
      </div>
    </div>
  );
}

// ── Install ──────────────────────────────────────────────────────────────────
/**
 * Two buttons, the visitor's own store first.
 *
 * Each is a plain link to the listing — no state, no fetch between the tap and
 * the store, because every extra step on this screen is a place to lose
 * somebody who has already decided. The referral credit rides on the SAME tap
 * as a fire-and-forget POST with `keepalive`, so navigating away does not
 * cancel it and a slow API cannot delay the store.
 *
 * A desktop visitor gets both buttons too (the App Store listing opens fine in
 * a browser, and Google Play can install to a signed-in phone from the web)
 * plus one line saying the page is easier on the phone itself.
 */
function Install({ referCode }) {
  const os = detectOs();
  const credited = useRef(false);

  // Once per visit. A visitor who taps iPhone, comes back, and taps Android is
  // one person arriving under one link; the server would answer the second
  // with `credited: false` anyway, but not sending it is cheaper and clearer.
  const creditReferral = useCallback(() => {
    if (!referCode || credited.current) return;
    credited.current = true;
    try {
      fetch(`${API_BASE}/api/referral`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: referCode }),
      }).catch(() => {});
    } catch { /* the tap is the point; the count is a courtesy */ }
  }, [referCode]);

  const apple = (
    <a key="ios" className={`mb-store${os === 'ios' ? ' mine' : ''}`} href={APP_STORE_URL}
       onClick={creditReferral} data-store="ios">
      <AppleIcon size={30} />
      <span><small>Download on the</small><b>App Store</b></span>
    </a>
  );
  const play = (
    <a key="android" className={`mb-store${os === 'android' ? ' mine' : ''}`} href={PLAY_STORE_URL}
       onClick={creditReferral} data-store="android">
      <PlayIcon size={28} />
      <span><small>Get it on</small><b>Google Play</b></span>
    </a>
  );

  return (
    <section className="mb-install" id="get">
      <h2 style={S.h2}>Get the app</h2>
      <p className="mb-lede">
        Free on the App Store and Google Play. No sign-up, no email — just install it and open it.
      </p>
      <div className="mb-stores">
        {os === 'android' ? [play, apple] : [apple, play]}
      </div>
      {os === null && (
        <p className="mb-fine">
          On a computer? Open <b>myumaps.ca/mobile</b> on your phone, or search the store
          for <b>UMaps</b>.
        </p>
      )}
      <BetaNote os={os} />
    </section>
  );
}

/**
 * For the beta testers only, folded: the beta build does not update itself
 * into the release, and on Android a tester still enrolled is served the beta
 * AGAIN if they reinstall. Same steps as the app's own sunset screen.
 */
function BetaNote({ os }) {
  const showAndroid = os !== 'ios';
  const showIos = os !== 'android';
  return (
    <details className="mb-beta">
      <summary>Had the beta? Here&rsquo;s how to switch</summary>
      {showIos && (
        <div className="mb-beta-os">
          {showAndroid && <div className="mb-beta-h">iPhone</div>}
          <Steps steps={[
            <>Delete the beta — the one you installed through <b>TestFlight</b>. It won&rsquo;t update into the real app.</>,
            <>Install <b>UMaps</b> from the App Store button above.</>,
          ]} />
        </div>
      )}
      {showAndroid && (
        <div className="mb-beta-os">
          {showIos && <div className="mb-beta-h">Android</div>}
          <Steps steps={[
            <><a className="mb-link" href={PLAY_LEAVE_BETA_URL}>Leave the beta program</a> and confirm on the page that opens. Skip this and Google Play installs the beta again.</>,
            <>Uninstall the beta version of UMaps.</>,
            <>Install <b>UMaps</b> from the Google Play button above.</>,
          ]} />
        </div>
      )}
    </details>
  );
}

function Steps({ steps }) {
  return (
    <ol className="mb-steps">
      {steps.map((s, n) => <li key={n}><span className="mb-num">{n + 1}</span><span>{s}</span></li>)}
    </ol>
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────
function Chevron({ dir }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points={dir === 'left' ? '15 5 8 12 15 19' : '9 5 16 12 9 19'} />
    </svg>
  );
}

function AppleIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.4 12.7c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9s-1.8-.8-3-.8c-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.6 2.3 2.8 2.2 1.1 0 1.6-.7 2.9-.7s1.7.7 2.9.7 2-1.1 2.7-2.2c.9-1.2 1.2-2.4 1.2-2.5 0 0-2.4-.9-2.4-3.6zM14.2 5.9c.6-.8 1-1.8.9-2.9-.9 0-2 .6-2.6 1.4-.6.7-1.1 1.7-.9 2.8 1 0 2-.5 2.6-1.3z" />
    </svg>
  );
}

/** The Play triangle, in the store's four colours, so the button reads as the store and not as "Android". */
function PlayIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.6 2.4 13.3 12 3.6 21.6c-.3-.2-.5-.6-.5-1.1V3.5c0-.5.2-.9.5-1.1z" fill="#2196f3" />
      <path d="M13.3 12 3.6 2.4c.3-.2.7-.2 1.1 0l11.6 6.6L13.3 12z" fill="#4caf50" />
      <path d="m16.3 9 3.9 2.2c.8.4.8 1.2 0 1.6L16.3 15 13.3 12 16.3 9z" fill="#ffc107" />
      <path d="M13.3 12l3 3-11.6 6.6c-.4.2-.8.2-1.1 0L13.3 12z" fill="#f44336" />
    </svg>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
// The page frame is inline (one-off, never reused); everything with a state or
// a breakpoint is in the stylesheet below, because inline styles cannot express
// :hover, :active or a media query.
const S = {
  page: {
    minHeight: '100vh',
    // Light and warm rather than the deep navy this started as: the page is
    // read outdoors on a phone, and it sits beside the rest of the site, which
    // is white with #1a73e8 on it.
    background: 'linear-gradient(180deg, #eef4ff 0%, #f7f9fc 46%, #ffffff 100%)',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    color: '#16223a',
    padding: '0 0 calc(28px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box',
    // A backstop, not the fix: the carousel rail is what actually stays inside
    // the viewport, by being a scroller.
    overflowX: 'hidden',
  },
  wrap: { width: 'min(560px, 100%)', margin: '0 auto', padding: '18px 18px 0', boxSizing: 'border-box' },
  header: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 },
  beta: {
    fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
    background: '#e5edfd', border: '1px solid #c9dbfb', color: '#1a56c4',
    borderRadius: 999, padding: '3px 9px',
  },
  h1: {
    fontSize: 'clamp(29px, 8.5vw, 40px)', lineHeight: 1.1, fontWeight: 900,
    letterSpacing: '-0.03em', margin: '0 0 12px', color: '#0f1e38',
  },
  sub: { fontSize: 15.5, lineHeight: 1.5, color: '#54617a', margin: '0 0 22px' },
  // Quiet, not a banner shouting about a promotion: the visitor came for the
  // app, and this is context rather than the offer.
  referred: {
    background: '#eaf2ff', border: '1px solid #cfe0ff', borderRadius: 12,
    padding: '10px 14px', margin: '0 0 16px',
    fontSize: 13.5, lineHeight: 1.45, color: '#1a4a9c', fontWeight: 600,
  },
  h2: { fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', margin: '0 0 12px', color: '#0f1e38' },
  footer: {
    display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center',
    marginTop: 28, paddingTop: 18, borderTop: '1px solid #e3e8f0',
  },
  footLink: { color: '#5f6b82', fontSize: 13, fontWeight: 600, textDecoration: 'none' },
};

const CSS = `
.mb-carousel { position: relative; margin: 0 0 26px; }

/* Hidden on a phone, where the finger is the control. Sits over the faded
   neighbours rather than beside the rail, so it costs the screenshots no width. */
.mb-arrow {
  display: none;
  position: absolute; top: 34%; z-index: 2;
  width: 42px; height: 42px; border-radius: 999px;
  align-items: center; justify-content: center;
  border: 1px solid #dbe3f0; background: rgba(255,255,255,0.92); color: #1a56c4;
  cursor: pointer; box-shadow: 0 3px 12px rgba(20,40,80,0.14);
  transition: background .16s ease, transform .1s ease;
  -webkit-tap-highlight-color: transparent;
}
.mb-arrow:hover { background: #fff; }
.mb-arrow:active { transform: scale(0.94); }
.mb-arrow.prev { left: 4px; }
.mb-arrow.next { right: 4px; }

/* ── The peek rail ──
   A native scroll-snap scroller, not a transform track: on a phone this is what
   gives momentum swiping, rubber-banding and per-pixel finger tracking, none of
   which a touch handler imitates convincingly.

   The side padding is what lets the FIRST and LAST slides reach the middle —
   without it neither end can centre, and the carousel appears to jam at both
   ends. 19% each side against a 62% slide leaves the neighbours showing evenly. */
.mb-rail {
  /* ⚠️ ONE LENGTH, used by both the slide width and the side padding.
     They were a percentage each before, and percentages here resolve against
     DIFFERENT boxes: flex-basis against the rail's content box (inside the
     padding), padding against the containing block's width. So the two could
     never agree — on a 1440px screen the "48%" slide came out 121px wide and
     sat 65px left of centre, because the padding that was meant to centre it
     was computed from a width the slide never had. A length has no such
     ambiguity: the padding is exactly half the leftover, so the first and last
     slides centre perfectly. */
  --slide: min(62vw, 260px);
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  padding: 4px calc(50% - var(--slide) / 2) 6px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.mb-rail::-webkit-scrollbar { display: none; }

.mb-slide {
  flex: 0 0 var(--slide);
  /* ⚠️ min-width:0 is load-bearing. A flex item defaults to min-width:auto,
     which is its MIN-CONTENT width — and the min-content of a slide is the
     screenshot's intrinsic 640px, so without this each slide refuses to shrink
     and the rail becomes several screens wide. */
  min-width: 0;
  scroll-snap-align: center;
  margin: 0; padding: 0; border: none; background: none;
  cursor: pointer;
  /* The faded, shrunken state IS the default — only the centred one is lifted
     out of it, so nothing has to be reset as slides go by. */
  transform: scale(0.88);
  opacity: 0.42;
  filter: saturate(0.75);
  transition: transform .38s cubic-bezier(.4,0,.2,1), opacity .38s ease, filter .38s ease;
  -webkit-tap-highlight-color: transparent;
}
.mb-slide.on { transform: scale(1); opacity: 1; filter: none; }

.mb-shot {
  display: block; width: 100%; height: auto; border-radius: 16px;
  background: #fff;
  border: 1px solid #dfe6f1;
  box-shadow: 0 10px 26px rgba(20,40,80,0.13);
}
.mb-slide.on .mb-shot { box-shadow: 0 16px 38px rgba(20,40,80,0.2); }

/* Outside the rail: at 62% width there is no room for two lines under a phone
   screenshot, and a caption that scrolled with the images would be half-visible
   either side of the one being read. */
.mb-cap {
  display: flex; flex-direction: column; gap: 3px;
  padding: 14px 8px 0; text-align: center; min-height: 64px;
}
.mb-cap-title { font-size: 16.5px; font-weight: 800; letter-spacing: -0.01em; color: #0f1e38; }
.mb-cap-body { font-size: 13.5px; line-height: 1.45; color: #5f6b82; }

.mb-dots { display: flex; justify-content: center; gap: 7px; margin-top: 12px; }
.mb-dot {
  width: 7px; height: 7px; padding: 0; border: none; border-radius: 999px; cursor: pointer;
  background: #c6d2e6; transition: width .25s ease, background .25s ease;
  -webkit-tap-highlight-color: transparent;
}
/* A wider pill rather than a colour change alone — the current slide has to be
   findable at a glance on a bright phone screen outdoors. */
.mb-dot.on { width: 20px; background: #1a73e8; }

.mb-install {
  background: #fff;
  border: 1px solid #e1e8f2;
  border-radius: 20px; padding: 20px 18px;
  box-shadow: 0 6px 24px rgba(20,40,80,0.07);
}
.mb-lede { font-size: 14px; line-height: 1.5; color: #4a5670; margin: 0 0 14px; }

/* ── The two store buttons ──
   Dark, like the stores' own badges, so they read as "the store" rather than as
   two more of the page's blue controls. Stacked, full width: these are the two
   things the whole page is for, tapped with a thumb, often one-handed — 60px
   is well over the 44px minimum. The visitor's own store is listed first and
   carries the accent ring; the other is still a button, never greyed. */
.mb-stores { display: flex; flex-direction: column; gap: 10px; }
.mb-store {
  display: flex; align-items: center; gap: 14px;
  min-height: 60px; padding: 10px 18px;
  border-radius: 14px; border: 1.5px solid #0f1e38;
  background: #0f1e38; color: #fff; text-decoration: none;
  transition: background .18s ease, transform .1s ease, box-shadow .18s ease;
  -webkit-tap-highlight-color: transparent;
}
.mb-store span { display: flex; flex-direction: column; line-height: 1.15; }
.mb-store small { font-size: 11.5px; font-weight: 600; letter-spacing: 0.01em; color: #c9d3e6; }
.mb-store b { font-size: 19px; font-weight: 800; letter-spacing: -0.01em; }
.mb-store:hover { background: #1b2b4b; border-color: #1b2b4b; }
.mb-store:active { transform: scale(0.985); }
.mb-store.mine { box-shadow: 0 0 0 3px rgba(26,115,232,0.28); border-color: #1a73e8; }

/* ── For the beta testers, folded ──
   A <details>, because it is for a few hundred people and the campus should not
   have to read past it. The summary is the whole affordance; no icon. */
.mb-beta {
  margin-top: 16px; border-top: 1px solid #e8edf5; padding-top: 12px;
}
.mb-beta summary {
  cursor: pointer; font-size: 13.5px; font-weight: 700; color: #1a56c4;
  list-style: none; -webkit-tap-highlight-color: transparent;
}
.mb-beta summary::-webkit-details-marker { display: none; }
.mb-beta summary::before { content: '▸ '; font-size: 11px; }
.mb-beta[open] summary::before { content: '▾ '; }
.mb-beta-os { margin-top: 12px; }
.mb-beta-h { font-size: 12px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #6b7788; margin: 0 0 6px; }
.mb-link { color: #1a56c4; font-weight: 700; }

.mb-fine { font-size: 12.5px; line-height: 1.5; color: #6b7788; margin: 12px 0 0; }

.mb-steps { list-style: none; margin: 0 0 16px; padding: 0; display: flex; flex-direction: column; gap: 11px; }
.mb-steps li { display: flex; gap: 11px; font-size: 14px; line-height: 1.5; color: #37435c; }
.mb-num {
  flex: none; width: 22px; height: 22px; border-radius: 999px;
  background: #e5edfd; border: 1px solid #c9dbfb; color: #1a56c4;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800; margin-top: 1px;
}

/* ── Desktop ──
   The rest of the page is a 560px reading column, which is right for prose and
   wrong for a gallery: the carousel there could only ever show one phone and
   two slivers. So on a wide screen the carousel alone breaks out of the column
   and centres itself, wide enough to hold three phones with the middle one
   lifted. A 50% left margin plus a half-width translate is what centres a block
   WIDER than its parent without knowing either width. */
@media (min-width: 620px) {
  .mb-carousel { width: min(880px, 94vw); margin-left: 50%; transform: translateX(-50%); }
  .mb-rail { --slide: 240px; gap: 18px; }
  .mb-arrow { display: flex; }
}
@media (prefers-reduced-motion: reduce) {
  .mb-rail { scroll-behavior: auto; }
  .mb-slide { transition: none; }
}
`;
